import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Logo } from "../components/Logo";
import { LeadFilters } from "../components/admin/LeadFilters";
import { LeadList } from "../components/admin/LeadList";
import { LeadDetail } from "../components/admin/LeadDetail";
import { api, formatApiError } from "../lib/api";

const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

export default function AdminInbox({ user, onLogout }) {
  const [filters, setFilters] = useState({ type: "", status: "", q: "", date_from: "", date_to: "" });
  const [data, setData] = useState({ items: [], counts: {}, statusCounts: {} });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(false);

  const onNotes = (lead, notes) => {
    setOpen((o) => (o && o.id === lead.id ? { ...o, notes } : o));
    setData((d) => ({ ...d, items: d.items.map((x) => (x.id === lead.id ? { ...x, notes } : x)) }));
  };

  const onStatus = async (lead, status) => {
    setSaving(true);
    try {
      await api.patch(`/admin/quotes/${lead.id}/status`, { status });
      const prev = lead.status;
      setOpen({ ...lead, status });
      setData((d) => ({
        ...d,
        items: d.items.map((x) => (x.id === lead.id ? { ...x, status } : x)),
        statusCounts: { ...d.statusCounts, [prev]: (d.statusCounts[prev] ?? 1) - 1, [status]: (d.statusCounts[status] ?? 0) + 1 },
      }));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/admin/quotes", { params: clean(filters) });
        setData({ items: Array.isArray(d?.items) ? d.items : [], counts: d?.counts ?? {}, statusCounts: d?.status_counts ?? {} });
      } catch (err) {
        if (err?.response?.status === 401) onLogout();
        else toast.error(formatApiError(err?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [filters, onLogout]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/quotes/export", { params: clean(filters), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anchorwell-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const logout = async () => { await api.post("/auth/logout").catch(() => {}); onLogout(); };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-inbox-page">
      <header className="bg-[#071510] py-4">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 flex items-center justify-between">
          <a href="/" data-testid="admin-home-link"><Logo light /></a>
          <div className="flex items-center gap-4 text-sm text-[#F4F1EA]">
            <span className="hidden sm:inline text-[#F4F1EA]/75">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 hover:bg-white/10 transition-colors"><LogOut className="w-4 h-4" /> Log out</button>
          </div>
        </div>
      </header>
      <main className="max-w-[1300px] mx-auto px-5 sm:px-8 py-10 lg:py-14">
        <p className="font-mono-d text-[11px] uppercase tracking-[0.22em] text-[#A8701F]">Private</p>
        <h1 className="font-serif-d text-4xl sm:text-5xl text-[#0B281E] mt-2">Lead Inbox</h1>
        <p className="text-[#52635B] mt-2" data-testid="lead-total">{loading ? "Loading…" : `${data.items.length} request${data.items.length === 1 ? "" : "s"} shown`}</p>
        <div className="mt-8"><LeadFilters filters={filters} setFilters={setFilters} counts={data.counts} statusCounts={data.statusCounts} onExport={exportCsv} exporting={exporting} /></div>
        <div className="mt-6 rounded-[24px] bg-white border border-[#0B281E]/10 overflow-hidden">
          <div className="hidden md:grid grid-cols-[110px_80px_1.2fr_1.4fr_1.6fr_100px] gap-x-4 px-4 py-3 bg-[#F4F1EA] font-mono-d text-[11px] uppercase tracking-[0.15em] text-[#52635B]">
            <span>Date</span><span>Type</span><span>Name</span><span>Contact</span><span>Details</span><span className="justify-self-end">Status</span>
          </div>
          <LeadList items={data.items} onOpen={setOpen} />
        </div>
      </main>
      <LeadDetail lead={open} onClose={() => setOpen(null)} onStatus={onStatus} saving={saving} onNotes={onNotes} />
    </div>
  );
}
